import React, { useState } from 'react';
import { Alert, Box, CircularProgress, Modal, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import styles from './styles';
import LookingToBuyOrSellFrom from './LookingToBuyOrSellFrom';
import { FormData } from 'src/interfaces/iproptypes';
import Navbar from './Navbar';
import Timeline from './Timeline';
import PriceRange from './PriceRange';
import PersonalInfo from './PersonalInfo';
import Header from './Header';
import useSendApi from './useSendApi';
import { COLOR_SUCCESS } from 'src/utils/colors';

const Body = ({ component }: { component: React.ReactNode }) => {
  return (
    <Box sx={{ ...styles.contentBox, minHeight: '60px', flex: 2, overflowY: 'auto', p: '10px' }}>
      {component}
    </Box>
  );
};

const CloseButton = ({ handleClose }: { handleClose: () => void }) => {
  return (
    <CloseIcon
      sx={{
        position: 'absolute',
        top: '5px',
        right: '5px',
        cursor: 'pointer',
        borderRadius: '900px',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
      }}
      onClick={handleClose}
    />
  );
};

const MultiStepFrom = ({
  location,
  setIsOpen,
}: {
  location: string;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [formData, setFormData] = useState<FormData>({
    lookingTo: '',
    timeline: '',
    priceRange: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredContactMethod: '',
    additionalInfo: '',
  });
  const { isLoading, error, data, sendApi, setError } = useSendApi();

  const pageIndexComponentMap = {
    0: <LookingToBuyOrSellFrom location={location} formData={formData} setFormData={setFormData} />,
    1: <Timeline formData={formData} setFormData={setFormData} />,
    2: <PriceRange formData={formData} setFormData={setFormData} />,
    3: <PersonalInfo formData={formData} setFormData={setFormData} />,
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = () => {
    if(!formData.firstName || !formData.email || !formData.phone || formData.phone.length < 10) {
      return;
    }
    console.log(JSON.stringify(formData, null, 4));
    sendApi(formData);    
  };

  return (
    <Modal
      open={true}
      onClose={handleClose}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <Box
        sx={{
          width: 'clamp(300px, 80vw, 800px)',
          height: 'clamp(300px, 80vh, 800px)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '20px',
          position: 'relative',
          outline: 'none',
          border: 'none',
          gap: '20px',
        }}
      >
        <CloseButton handleClose={handleClose} />
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
          >
            <CircularProgress size={80} />
          </Box>
        ) : !error && data && data.success ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}
          >
            <Typography variant="body1" textAlign="center">
              <Stack direction="column" alignItems="center" gap={1}>
                <CheckCircleRoundedIcon sx={{ fontSize: 40, color: COLOR_SUCCESS }} />
                Form submitted successfully!
              </Stack>
            </Typography>
          </Box>
        ) : (
          <>
            <Header activeStep={pageIndex} />
            <Body component={pageIndexComponentMap[pageIndex]} />
            <Navbar
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              maxPageIndex={Object.keys(pageIndexComponentMap).length - 1}
              handleSubmit={handleSubmit}
              formData={formData}
            />
          </>
        )}
      </Box>
    </Modal>
  );
};

export default MultiStepFrom;
